import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AssetThumb } from "@/components/assets/AssetThumb";
import { BrandModelFields } from "@/components/assets/BrandModelFields";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/common/DatePicker";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useHousehold } from "@/context/HouseholdContext";
import { CATEGORY_LABELS, useRooms } from "@/hooks/use-assets";
import { findBrand } from "@/lib/brands";
import { api, formatApiError } from "@/lib/api";

const EMPTY = { name: "", category: "other", room: "", brand: "", model: "", serial_number: "", purchase_date: "", purchase_price: "", warranty_end: "", notes: "", photo_id: null, value_override: "", depreciation_rate: "" };
const RATES = { computer: 25, multimedia: 20, appliance: 15, vehicle: 15, sport: 15, other: 15, furniture: 10, tools: 10, installation: 7, jewelry: 0, art: 0 };
const numOrNull = (x) => (x === "" || x == null ? null : Number(x));
const clean = (v) => Object.fromEntries(Object.entries(v).map(([k, x]) => [k, x === "" ? null : x]));

const F = ({ id, label, children }) => <div><Label htmlFor={id}>{label}</Label><div className="mt-1.5">{children}</div></div>;

export const AssetFormDialog = ({ open, onOpenChange, initial, asset, onSaved }) => {
  const { active } = useHousehold();
  const { data: rooms = [] } = useRooms();
  const qc = useQueryClient();
  const [v, setV] = useState({ ...EMPTY, ...initial, ...asset });
  const set = (k) => (e) => setV({ ...v, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!v.room) { toast.error("Choisissez la pièce où se trouve l'équipement"); return; }
    const body = clean({ ...v, purchase_price: numOrNull(v.purchase_price), value_override: numOrNull(v.value_override), depreciation_rate: numOrNull(v.depreciation_rate) });
    ["id", "created_at", "updated_at", "room_reason", "confidence", "current_value", "auto_value", "value_source", "rate_effective", "warranty_days_left", "warranty_status"].forEach((k) => delete body[k]);
    try {
      const { data } = asset ? await api.patch(`/households/${active.id}/assets/${asset.id}`, body) : await api.post(`/households/${active.id}/assets`, body);
      qc.invalidateQueries({ queryKey: ["assets", active.id] });
      qc.invalidateQueries({ queryKey: ["rooms", active.id] });
      qc.invalidateQueries({ queryKey: ["dashboard", active.id] });
      qc.invalidateQueries({ queryKey: ["maintenance", active.id] });
      toast.success(asset ? "Équipement mis à jour" : "Équipement ajouté");
      onOpenChange(false);
      onSaved?.(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="asset-form-dialog">
        <DialogHeader><DialogTitle className="font-heading">{asset ? "Modifier l'équipement" : "Nouvel équipement"}</DialogTitle></DialogHeader>
        {initial?.room_reason && (
          <div className="tone-wash tone-sky rounded-xl border border-border p-3 text-xs" data-testid="asset-ai-suggestion">
            Suggestion IA ({Math.round((initial.confidence || 0) * 100)} %) : pièce « {initial.room || "?"} » — {initial.room_reason.replace(/\.\s*$/, "")}. Vérifiez avant d'enregistrer.
          </div>
        )}
        <form onSubmit={submit} className="space-y-4">
          {v.photo_id && <AssetThumb photoId={v.photo_id} className="h-40 w-full" />}
          <div className="rounded-xl bg-muted/60 px-3 py-2 text-xs" data-testid="asset-household">Foyer : <b>{active?.name}</b></div>
          <F id="a-name" label="Nom"><Input id="a-name" value={v.name || ""} onChange={set("name")} required minLength={2} data-testid="asset-name-input" /></F>
          <div className="grid grid-cols-2 gap-3">
            <F id="a-cat" label="Catégorie">
              <Select value={v.category} onValueChange={(c) => setV((s) => {
                const b = findBrand(c, s.brand);
                return { ...s, category: c, brand: b ? b.name : "", model: b && b.models.includes(s.model) ? s.model : "" };
              })}>
                <SelectTrigger data-testid="asset-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(CATEGORY_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F id="a-room" label="Pièce *">
              <Select value={v.room || ""} onValueChange={set("room")}>
                <SelectTrigger data-testid="asset-room-select"><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent>{rooms.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <BrandModelFields key={v.category} category={v.category} brand={v.brand} model={v.model} onChange={(p) => setV((s) => ({ ...s, ...p }))} />
            <F id="a-date" label="Date d'achat"><DatePicker id="a-date" value={v.purchase_date || ""} onChange={set("purchase_date")} placeholder="Date d'achat" data-testid="asset-date-input" /></F>
            <F id="a-price" label="Prix d'achat"><Input id="a-price" type="number" min="0" step="0.01" value={v.purchase_price ?? ""} onChange={set("purchase_price")} data-testid="asset-price-input" /></F>
          </div>
          <F id="a-warranty" label="Fin de garantie"><DatePicker id="a-warranty" value={v.warranty_end || ""} onChange={set("warranty_end")} placeholder="Fin de garantie" data-testid="asset-warranty-input" /></F>
          <div className="grid grid-cols-2 gap-3">
            <F id="a-rate" label="Décote annuelle %"><Input id="a-rate" type="number" min="0" max="100" step="0.5" value={v.depreciation_rate ?? ""} onChange={set("depreciation_rate")} placeholder={`${RATES[v.category]} % (catégorie)`} data-testid="asset-rate-input" /></F>
            <F id="a-value" label="Valeur actuelle (manuelle)"><Input id="a-value" type="number" min="0" step="0.01" value={v.value_override ?? ""} onChange={set("value_override")} placeholder="Calcul automatique" data-testid="asset-value-input" /></F>
          </div>
          <F id="a-serial" label="N° de série"><Input id="a-serial" value={v.serial_number || ""} onChange={set("serial_number")} data-testid="asset-serial-input" /></F>
          <F id="a-notes" label="Notes"><Textarea id="a-notes" value={v.notes || ""} onChange={set("notes")} data-testid="asset-notes-input" /></F>
          <Button type="submit" className="w-full rounded-full" data-testid="asset-save-button">Enregistrer</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
